import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import PortalBottomNav from "../components/PortalBottomNav";
import PortalHeader from "../components/PortalHeader";
import {
  Home,
  Users,
  Bell,
  User,
  MapPin,
  ArrowLeft,
  Plus,
  X,
  Coffee,
  Utensils,
  ShoppingBag,
  Waves,
  Mountain,
  Sparkles,
  Moon,
  Laptop,
  Loader2,
  ExternalLink,
  Trash2,
  Navigation
} from "lucide-react";

// Opens the user's sat nav (Google Maps app on mobile) with turn-by-turn directions
const directionsUrl = (place) => {
  let url = `https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}&travelmode=driving`;
  if (place.place_id) url += `&destination_place_id=${place.place_id}`;
  return url;
};

const openInMapsUrl = (place) =>
  place.place_id
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name)}&query_place_id=${place.place_id}`
    : `https://www.google.com/maps/search/?api=1&query=${place.lat},${place.lng}`;

const VILLA_SAVI_COORDS = { lat: 39.6566473, lng: -0.395298 }; // Savi Coliving Valencia, Náquera

const CATEGORY_CONFIG = {
  coffee: { icon: Coffee, emoji: "☕", color: "#8B4513", name: "Coffee" },
  restaurant: { icon: Utensils, emoji: "🍽️", color: "#C85A38", name: "Restaurants" },
  shopping: { icon: ShoppingBag, emoji: "🛍️", color: "#9C27B0", name: "Shopping" },
  beach: { icon: Waves, emoji: "🏖️", color: "#00BCD4", name: "Beaches" },
  hiking: { icon: Mountain, emoji: "🥾", color: "#4CAF50", name: "Hiking" },
  things_to_do: { icon: Sparkles, emoji: "🎯", color: "#FF9800", name: "Things to Do" },
  nightlife: { icon: Moon, emoji: "🌙", color: "#673AB7", name: "Nightlife" },
  coworking: { icon: Laptop, emoji: "💻", color: "#2196F3", name: "Coworking" }
};

const AreaGuidePage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const clickMarkerRef = useRef(null);
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingPlace, setAddingPlace] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [clickedLocation, setClickedLocation] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const [newPlace, setNewPlace] = useState({
    name: "",
    category: "coffee",
    description: "",
    recommendation: "",
    address: "",
    website: "",
    place_id: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchPlaces();
  }, [token]);

  const fetchPlaces = async () => {
    try {
      const [placesRes, notificationsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/places`, { headers }),
        axios.get(`${API}/notifications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/announcements`, { headers }).catch(() => ({ data: [] }))
      ]);
      setPlaces(placesRes.data || []);
      
      // Calculate unread count
      const unreadNotifs = (notificationsRes.data || []).filter(n => !n.read).length;
      const unreadAnns = (announcementsRes.data || []).filter(a => !a.read).length;
      setUnreadCount(unreadNotifs + unreadAnns);
    } catch (error) {
      console.error("Error fetching places:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load Google Maps script on mount
  useEffect(() => {
    // Set a timeout to catch silent failures
    const timeout = setTimeout(() => {
      if (!window.google?.maps && !mapError) {
        setMapError("Google Maps failed to load. Please check API key configuration.");
        setLoading(false);
      }
    }, 10000); // 10 second timeout
    
    // Check if script already exists
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Script exists, check if API is ready
      if (window.google?.maps) {
        setMapLoaded(true);
        clearTimeout(timeout);
      }
      return () => clearTimeout(timeout);
    }
    
    // Add Google Maps script
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setMapError("Google Maps API key not configured");
      setLoading(false);
      clearTimeout(timeout);
      return () => clearTimeout(timeout);
    }
    
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onerror = () => {
      setMapError("Failed to load Google Maps");
      setLoading(false);
      clearTimeout(timeout);
    };
    script.onload = () => {
      setMapLoaded(true);
      clearTimeout(timeout);
    };
    document.head.appendChild(script);
    
    return () => clearTimeout(timeout);
  }, [mapError]);

  // Initialize map when Google Maps is ready and mapRef is available
  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstanceRef.current) return; // Already initialized
    if (mapError) return; // Don't try to init if there's an error
    
    const initializeMap = () => {
      if (!window.google?.maps?.Map) return false;
      
      try {
        const map = new window.google.maps.Map(mapRef.current, {
          center: VILLA_SAVI_COORDS,
          zoom: 12,
          styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }
          ],
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true
        });

        // Add Villa Savi marker
        new window.google.maps.Marker({
          position: VILLA_SAVI_COORDS,
          map,
          title: "Villa Savi",
          icon: {
            url: "data:image/svg+xml," + encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
                <circle cx="22" cy="22" r="20" fill="#C85A38" stroke="white" stroke-width="3"/>
                <text x="22" y="28" text-anchor="middle" fill="white" font-size="18">🏠</text>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(44, 44),
            anchor: new window.google.maps.Point(22, 22)
          }
        });

        // Click to add new place
        map.addListener("click", (e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          setClickedLocation({ lat, lng });
          setShowAddForm(true);
          
          // Remove previous click marker
          if (clickMarkerRef.current) {
            clickMarkerRef.current.setMap(null);
          }
          
          // Add new click marker
          clickMarkerRef.current = new window.google.maps.Marker({
            position: { lat, lng },
            map,
            icon: {
              url: "data:image/svg+xml," + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="16" fill="#C85A38" stroke="white" stroke-width="2"/>
                  <text x="18" y="24" text-anchor="middle" fill="white" font-size="14">📍</text>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(36, 36),
              anchor: new window.google.maps.Point(18, 18)
            },
            animation: window.google.maps.Animation.DROP
          });
        });

        mapInstanceRef.current = map;
        setMapLoaded(true);
        setMapReady(true);
        setMapError(null);
        return true;
      } catch (error) {
        console.error("Error initializing map:", error);
        setMapError("Failed to load map");
        return false;
      }
    };

    // Try to initialize immediately
    if (initializeMap()) return;
    
    // Otherwise poll for Google Maps to be ready
    const checkInterval = setInterval(() => {
      if (initializeMap()) {
        clearInterval(checkInterval);
      }
    }, 200);
    
    // Timeout after 15 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      if (!mapInstanceRef.current) {
        setMapError("Map took too long to load");
      }
    }, 15000);
    
    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [loading]); // Re-run when loading completes

  // Update markers when places or filter changes
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps || !mapReady) return;

    // Clear existing place markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Filter places
    const filteredPlaces = selectedCategory === "all" 
      ? places 
      : places.filter(p => p.category === selectedCategory);

    // Add new markers
    filteredPlaces.forEach(place => {
      const config = CATEGORY_CONFIG[place.category] || CATEGORY_CONFIG.things_to_do;
      
      const marker = new window.google.maps.Marker({
        position: { lat: place.lat, lng: place.lng },
        map: mapInstanceRef.current,
        title: place.name,
        icon: {
          url: "data:image/svg+xml," + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="${config.color}" stroke="white" stroke-width="2"/>
              <text x="18" y="24" text-anchor="middle" fill="white" font-size="14">${config.emoji}</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(36, 36),
          anchor: new window.google.maps.Point(18, 18)
        }
      });

      marker.addListener("click", () => {
        setSelectedPlace(place);
        mapInstanceRef.current.panTo({ lat: place.lat, lng: place.lng });
      });

      markersRef.current.push(marker);
    });

    // Fit the map to show the villa + all visible pins
    if (filteredPlaces.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(VILLA_SAVI_COORDS);
      filteredPlaces.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
      mapInstanceRef.current.fitBounds(bounds, 40);
    }
  }, [places, selectedCategory, mapReady]);

  const dropPin = (lat, lng) => {
    setClickedLocation({ lat, lng });
    if (!mapInstanceRef.current || !window.google?.maps) return;
    if (clickMarkerRef.current) clickMarkerRef.current.setMap(null);
    clickMarkerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current,
      icon: {
        url: "data:image/svg+xml," + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="16" fill="#C85A38" stroke="white" stroke-width="2"/>
            <text x="18" y="24" text-anchor="middle" fill="white" font-size="14">📍</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(36, 36),
        anchor: new window.google.maps.Point(18, 18)
      },
      animation: window.google.maps.Animation.DROP
    });
    mapInstanceRef.current.panTo({ lat, lng });
    if (mapInstanceRef.current.getZoom() < 13) mapInstanceRef.current.setZoom(13);
  };

  // Google Places search inside the add form — pick a place, everything fills itself
  useEffect(() => {
    if (!showAddForm || !window.google?.maps?.places || !searchInputRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(searchInputRef.current, {
      fields: ["name", "geometry", "formatted_address", "website", "place_id"],
      bounds: new window.google.maps.LatLngBounds(
        { lat: VILLA_SAVI_COORDS.lat - 0.6, lng: VILLA_SAVI_COORDS.lng - 0.6 },
        { lat: VILLA_SAVI_COORDS.lat + 0.6, lng: VILLA_SAVI_COORDS.lng + 0.6 }
      )
    });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place.geometry?.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      dropPin(lat, lng);
      setNewPlace((prev) => ({
        ...prev,
        name: place.name || prev.name,
        address: place.formatted_address || prev.address,
        website: place.website || prev.website,
        place_id: place.place_id || prev.place_id
      }));
    });
    autocompleteRef.current = ac;
    return () => {
      window.google.maps.event.clearInstanceListeners(ac);
      autocompleteRef.current = null;
    };
  }, [showAddForm]);

  const handleAddPlace = async (e) => {
    e.preventDefault();
    if (!clickedLocation) {
      toast.error("Please click on the map to select a location");
      return;
    }
    if (!newPlace.name.trim()) {
      toast.error("Please enter a place name");
      return;
    }

    setAddingPlace(true);
    try {
      const res = await axios.post(`${API}/places`, {
        ...newPlace,
        lat: clickedLocation.lat,
        lng: clickedLocation.lng
      }, { headers });

      if (res.data.success) {
        toast.success("Place added to the guide!");
        setPlaces([res.data.place, ...places]);
        setShowAddForm(false);
        setClickedLocation(null);
        
        // Remove click marker
        if (clickMarkerRef.current) {
          clickMarkerRef.current.setMap(null);
          clickMarkerRef.current = null;
        }
        
        setNewPlace({
          name: "",
          category: "coffee",
          description: "",
          recommendation: "",
          address: "",
          website: "",
          place_id: ""
        });
      }
    } catch (error) {
      toast.error("Failed to add place");
    } finally {
      setAddingPlace(false);
    }
  };

  const handleDeletePlace = async (placeId) => {
    try {
      await axios.delete(`${API}/places/${placeId}`, { headers });
      toast.success("Place removed");
      setPlaces(places.filter(p => p.id !== placeId));
      setSelectedPlace(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete place");
    }
  };

  const filteredPlaces = selectedCategory === "all" 
    ? places 
    : places.filter(p => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C85A38]" />
      </div>
    );
  }

  // If there's a map error, show a fallback UI that still allows navigation
  if (mapError) {
    return (
      <div className="min-h-screen bg-[#FBF7F1] pb-24 savi-bg-pattern">
        {/* Header — same strip as the rest of the portal */}
        <PortalHeader title="Local area guide" testid="guide-header" />

        <div className="max-w-2xl mx-auto px-4 py-8 text-center">
          <div className="bg-white border border-stone-200 rounded-xl p-8">
            <MapPin className="w-12 h-12 text-[#C85A38] mx-auto mb-4" />
            <h2 className="font-serif text-xl text-[#1A1A1A] mb-2">Map Unavailable</h2>
            <p className="text-[#1A1A1A]/60 mb-4">{mapError}</p>
            <p className="text-sm text-[#1A1A1A]/50 mb-6">
              The map feature requires a valid Google Maps API key configured for this domain.
            </p>
            <Link to="/dashboard">
              <Button className="bg-[#C85A38] hover:bg-[#B85540] text-white">
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>

        {/* Bottom Navigation */}
        <PortalBottomNav active="guide" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF7F1] pb-24 savi-bg-pattern">
      {/* Header — same strip as the rest of the portal */}
      <PortalHeader title="Local area guide" testid="guide-header" />

      <div className="px-4 pt-5 pb-1 relative z-10">
        <div className="max-w-2xl mx-auto">
          <p className="text-sm text-[#1A1A1A]/55">
            The best spots around Villa Savi — add your favourites in seconds.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-3 no-scrollbar">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              selectedCategory === "all"
                ? "bg-[#C85A38] text-white"
                : "bg-white text-[#1A1A1A]/70 border border-stone-200"
            }`}
          >
            All Places
          </button>
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                selectedCategory === key
                  ? "text-white"
                  : "bg-white text-[#1A1A1A]/70 border border-stone-200"
              }`}
              style={selectedCategory === key ? { backgroundColor: config.color } : {}}
            >
              <span>{config.emoji}</span>
              {config.name}
            </button>
          ))}
        </div>

        {/* Add Place row — outside the map so nothing overlaps */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="text-xs text-[#1A1A1A]/50">Tap a pin to see details</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-[#C85A38] hover:bg-[#A84830] text-white px-4 py-2 rounded-full shadow-sm flex items-center gap-2 transition-colors text-sm font-medium"
            data-testid="add-place-btn"
          >
            <Plus className="w-4 h-4" />
            Add Place
          </button>
        </div>

        {/* Map */}
        <div className="relative rounded-2xl overflow-hidden border border-stone-200 shadow-sm mb-4">
          <div 
            ref={mapRef} 
            className="w-full h-[350px] bg-stone-100"
            data-testid="area-guide-map"
          >
            {!mapLoaded && !mapError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#C85A38]" />
              </div>
            )}
            {mapError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-red-500">{mapError}</p>
              </div>
            )}
          </div>
        </div>

        {/* Add Place Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[85vh] overflow-y-auto">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-xl text-[#1A1A1A]">Add a Place</h3>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setClickedLocation(null);
                      if (clickMarkerRef.current) {
                        clickMarkerRef.current.setMap(null);
                        clickMarkerRef.current = null;
                      }
                    }}
                    className="p-2 hover:bg-stone-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Search — the easy way to add a place */}
                <div className="mb-4">
                  <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Search for the place</label>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder='Try "La Más Bonita" or "Café..."'
                    className="w-full h-11 px-3 rounded-xl border border-stone-200 bg-white text-[#1A1A1A] text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A38]/30"
                    data-testid="place-search-input"
                  />
                  <p className="text-xs text-[#1A1A1A]/50 mt-1">
                    Pick a result and we'll fill in the name, address & location automatically.
                  </p>
                </div>

                {!clickedLocation && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                    <p className="text-amber-800 text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      Search above — or close this and tap the map to drop a pin manually.
                    </p>
                  </div>
                )}

                {clickedLocation && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4">
                    <p className="text-green-800 text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      Location set! Add any extra details below.
                    </p>
                  </div>
                )}

                <form onSubmit={handleAddPlace} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Place Name *</label>
                    <Input
                      value={newPlace.name}
                      onChange={(e) => setNewPlace({ ...newPlace, name: e.target.value })}
                      placeholder="e.g., La Más Bonita Beach Bar"
                      className="rounded-xl"
                      data-testid="place-name-input"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Category *</label>
                    <select
                      value={newPlace.category}
                      onChange={(e) => setNewPlace({ ...newPlace, category: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-stone-200 bg-white text-[#1A1A1A]"
                      data-testid="place-category-select"
                    >
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>
                          {config.emoji} {config.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Description</label>
                    <Input
                      value={newPlace.description}
                      onChange={(e) => setNewPlace({ ...newPlace, description: e.target.value })}
                      placeholder="What is this place?"
                      className="rounded-xl"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Why do you recommend it?</label>
                    <Textarea
                      value={newPlace.recommendation}
                      onChange={(e) => setNewPlace({ ...newPlace, recommendation: e.target.value })}
                      placeholder="What did you like about it?"
                      className="rounded-xl min-h-[80px]"
                      data-testid="place-recommendation-input"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Address (optional)</label>
                    <Input
                      value={newPlace.address}
                      onChange={(e) => setNewPlace({ ...newPlace, address: e.target.value })}
                      placeholder="Street address"
                      className="rounded-xl"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Website (optional)</label>
                    <Input
                      value={newPlace.website}
                      onChange={(e) => setNewPlace({ ...newPlace, website: e.target.value })}
                      placeholder="https://..."
                      className="rounded-xl"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={addingPlace || !clickedLocation}
                    className="w-full bg-[#C85A38] hover:bg-[#A84830] rounded-full"
                    data-testid="submit-place-btn"
                  >
                    {addingPlace ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Add to Guide
                  </Button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Selected Place Detail */}
        {selectedPlace && (
          <div className="bg-white rounded-2xl p-5 border border-stone-200 shadow-sm mb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-xl"
                  style={{ backgroundColor: `${CATEGORY_CONFIG[selectedPlace.category]?.color}20` }}
                >
                  {CATEGORY_CONFIG[selectedPlace.category]?.emoji || "📍"}
                </div>
                <div>
                  <h3 className="font-medium text-[#1A1A1A] text-lg">{selectedPlace.name}</h3>
                  <p className="text-sm text-[#1A1A1A]/60">{selectedPlace.description}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedPlace(null)}
                className="p-1.5 hover:bg-stone-100 rounded-full"
              >
                <X className="w-4 h-4 text-[#1A1A1A]/50" />
              </button>
            </div>

            {selectedPlace.recommendation && (
              <div className="mt-4 bg-[#F8F6F3] rounded-xl p-4">
                <p className="text-sm text-[#1A1A1A]/80 italic">"{selectedPlace.recommendation}"</p>
                <p className="text-xs text-[#1A1A1A]/50 mt-2">— {selectedPlace.added_by_name}</p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <a
                href={directionsUrl(selectedPlace)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 bg-[#C85A38] hover:bg-[#A84830] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
                data-testid="place-directions-btn"
              >
                <Navigation className="w-4 h-4" />
                Directions
              </a>
              <a
                href={openInMapsUrl(selectedPlace)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-[#1A1A1A]/70 hover:text-[#C85A38] border border-stone-200 px-3 py-2 rounded-full transition-colors"
                data-testid="place-open-maps-btn"
              >
                <MapPin className="w-4 h-4" />
                Open in Google Maps
              </a>
              {selectedPlace.website && (
                <a
                  href={selectedPlace.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-[#C85A38] hover:underline px-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  Website
                </a>
              )}
              {(user?.email === selectedPlace.added_by_email || user?.is_admin || user?.role === "admin") && (
                <button
                  onClick={() => handleDeletePlace(selectedPlace.id)}
                  className="ml-auto flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
            </div>
          </div>
        )}

        {/* Places List */}
        <div className="space-y-3">
          <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#C85A38]" />
            {selectedCategory === "all" ? "All Places" : CATEGORY_CONFIG[selectedCategory]?.name}
            <span className="text-[#1A1A1A]/50 text-sm font-normal">({filteredPlaces.length})</span>
          </h3>

          {filteredPlaces.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-stone-100">
              <MapPin className="w-12 h-12 text-[#1A1A1A]/20 mx-auto mb-3" />
              <p className="text-[#1A1A1A]/60">No places added yet</p>
              <p className="text-[#1A1A1A]/40 text-sm mt-1">Be the first to share your favorites!</p>
            </div>
          ) : (
            filteredPlaces.map((place) => {
              const config = CATEGORY_CONFIG[place.category] || CATEGORY_CONFIG.things_to_do;
              return (
                <div
                  key={place.id}
                  onClick={() => {
                    setSelectedPlace(place);
                    if (mapInstanceRef.current) {
                      mapInstanceRef.current.panTo({ lat: place.lat, lng: place.lng });
                      mapInstanceRef.current.setZoom(14);
                    }
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="w-full bg-white rounded-xl p-4 border border-stone-100 hover:border-stone-200 transition-all text-left flex items-center gap-3 cursor-pointer"
                  data-testid={`place-${place.id}`}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                    style={{ backgroundColor: `${config.color}20` }}
                  >
                    {config.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#1A1A1A] truncate">{place.name}</div>
                    <div className="text-sm text-[#1A1A1A]/60 truncate">{place.description || place.recommendation}</div>
                    <div className="text-xs text-[#1A1A1A]/40 mt-0.5">by {place.added_by_name?.split(" ")[0]}</div>
                  </div>
                  <a
                    href={directionsUrl(place)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C85A38]/10 hover:bg-[#C85A38] text-[#C85A38] hover:text-white flex items-center justify-center transition-colors"
                    title="Get directions"
                    data-testid={`directions-${place.id}`}
                  >
                    <Navigation className="w-4 h-4" />
                  </a>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <PortalBottomNav active="guide" />
    </div>
  );
};

export default AreaGuidePage;
